import React, { useContext, useMemo, useState } from "react";
import { useQuery, gql } from "@apollo/client";
import {
  Box, Container, Typography, Paper, Stack, Grid,
  CircularProgress, Button,
} from "@mui/material";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import EventIcon from "@mui/icons-material/Event";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import HistoryIcon from "@mui/icons-material/History";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import { useNavigate } from "react-router-dom";
import { Header } from "../../components/Header";
import { myWalletBalance, myWalletTransactions, myStoreCredits } from "../../apollo/server";
import UserContext from "../../context/User";
import { resizedImage } from "../../utils/resizedImage";

const MY_WALLET_BALANCE = gql`${myWalletBalance}`;
const MY_WALLET_TXS = gql`${myWalletTransactions}`;
const MY_STORE_CREDITS = gql`${myStoreCredits}`;

function formatDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

const SOURCE_LABEL = {
  REFUND: "Refund",
  REFUND_BONUS: "Refund bonus",
  REFUND_RETURN: "Refund returned",
  ORDER_PAYMENT: "Used for order",
  ADMIN_ADJUSTMENT: "Admin credit",
  EXPIRY_DEDUCTION: "Expired",
};

const FILTERS = [
  { key: "ALL", label: "All", icon: HistoryIcon },
  { key: "CREDIT", label: "Credits", icon: ArrowUpwardIcon },
  { key: "DEBIT", label: "Debits", icon: ArrowDownwardIcon },
];

function TxCard({ tx }) {
  const isCredit = tx.type === "CREDIT";
  const expired = tx.expiresAt && new Date(tx.expiresAt) < new Date();
  const accent = isCredit ? "#16a34a" : "#dc2626";
  const sourceLabel = SOURCE_LABEL[tx.source] || tx.source;

  return (
    <Paper
      elevation={0}
      sx={{
        position: "relative",
        borderRadius: "16px",
        bgcolor: "#fff",
        border: "1px solid #e5e7eb",
        overflow: "hidden",
        opacity: expired ? 0.6 : 1,
        transition: "all 0.18s ease",
        boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
        "&:hover": {
          transform: "translateY(-1px)",
          boxShadow: "0 6px 18px rgba(15,23,42,0.08)",
          borderColor: accent,
        },
      }}
    >
      <Box sx={{ display: "flex" }}>
        {/* Left accent stripe */}
        <Box sx={{ width: 5, bgcolor: accent, flexShrink: 0 }} />

        <Box sx={{ flex: 1, p: 2 }}>
          <Stack direction="row" alignItems="flex-start" gap={1.5}>
            <Box sx={{
              width: 36, height: 36, borderRadius: "12px",
              bgcolor: isCredit ? "#dcfce7" : "#fee2e2",
              display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0,
            }}>
              {isCredit
                ? <ArrowUpwardIcon sx={{ fontSize: 18, color: accent }} />
                : <ArrowDownwardIcon sx={{ fontSize: 18, color: accent }} />}
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
                <Typography sx={{
                  fontWeight: 800, fontSize: "1rem", color: accent, letterSpacing: 0.2,
                }}>
                  {isCredit ? "+" : "−"}${parseFloat(tx.amount).toFixed(2)}
                </Typography>
                <Stack direction="row" alignItems="center" gap={0.4}>
                  <EventIcon sx={{ fontSize: 12, color: "#94a3b8" }} />
                  <Typography sx={{ fontSize: "0.72rem", color: "#94a3b8", fontWeight: 600 }}>
                    {formatDate(tx.createdAt)}
                  </Typography>
                </Stack>
              </Stack>

              <Stack direction="row" alignItems="center" gap={0.4} sx={{ mt: 0.3 }}>
                <ReceiptLongIcon sx={{ fontSize: 13, color: "#94a3b8" }} />
                <Typography sx={{ fontSize: "0.8rem", color: "#475569", fontWeight: 600 }}>
                  {sourceLabel}{tx.orderId ? ` · Order #${tx.orderId}` : ""}{tx.restaurantName ? ` · Store credit at ${tx.restaurantName}` : ""}
                </Typography>
              </Stack>

              {tx.note && (
                <Typography sx={{ fontSize: "0.78rem", color: "#6b7280", fontStyle: "italic", mt: 0.4 }}>
                  {tx.note}
                </Typography>
              )}

              {isCredit && tx.expiresAt && (
                <Stack direction="row" alignItems="center" gap={0.4} sx={{ mt: 0.5 }}>
                  <AccessTimeIcon sx={{ fontSize: 12, color: expired ? "#dc2626" : "#94a3b8" }} />
                  <Typography sx={{
                    fontSize: "0.7rem", fontWeight: 600,
                    color: expired ? "#dc2626" : "#94a3b8",
                  }}>
                    {expired ? "Expired" : `Expires ${formatDate(tx.expiresAt)}`}
                  </Typography>
                </Stack>
              )}
            </Box>
          </Stack>
        </Box>
      </Box>
    </Paper>
  );
}

function StatCard({ icon, label, value, accent }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2, borderRadius: "16px", border: "1px solid #e5e7eb",
        bgcolor: "#fff", height: "100%",
      }}
    >
      <Stack direction="row" alignItems="center" gap={1.25}>
        <Box sx={{
          width: 38, height: 38, borderRadius: "12px",
          bgcolor: `${accent}14`,
          display: "flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0,
        }}>
          {React.cloneElement(icon, { sx: { color: accent, fontSize: 20 } })}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.5 }}>
            {label}
          </Typography>
          <Typography sx={{ fontSize: "1.15rem", fontWeight: 800, color: "#111827", lineHeight: 1.2 }}>
            {value}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );
}

export default function Wallet() {
  const navigate = useNavigate();
  const { isLoggedIn } = useContext(UserContext);
  const [filter, setFilter] = useState("ALL");

  const { data: balData, loading: balLoading } = useQuery(MY_WALLET_BALANCE, { skip: !isLoggedIn });
  const { data: txData, loading: txLoading } = useQuery(MY_WALLET_TXS, {
    variables: { page: 1, limit: 30 }, skip: !isLoggedIn,
  });

  const { data: storeData } = useQuery(MY_STORE_CREDITS, { skip: !isLoggedIn, fetchPolicy: "cache-and-network" });
  const storeCredits = storeData?.myStoreCredits ?? [];

  const balance = parseFloat(balData?.myWalletBalance ?? 0);
  const transactions = txData?.myWalletTransactions ?? [];

  // earliest non-expired credit expiry
  const nextExpiry = useMemo(
    () => transactions
      .filter(t => t.type === "CREDIT" && t.expiresAt && new Date(t.expiresAt) > new Date())
      .sort((a, b) => new Date(a.expiresAt) - new Date(b.expiresAt))[0],
    [transactions]
  );

  const totals = useMemo(() => {
    let credits = 0, debits = 0;
    for (const t of transactions) {
      const amt = parseFloat(t.amount) || 0;
      if (t.type === "CREDIT") credits += amt;
      else debits += amt;
    }
    return { credits, debits };
  }, [transactions]);

  const counts = useMemo(() => ({
    ALL: transactions.length,
    CREDIT: transactions.filter(t => t.type === "CREDIT").length,
    DEBIT: transactions.filter(t => t.type === "DEBIT").length,
  }), [transactions]);

  const filtered = useMemo(() => {
    if (filter === "ALL") return transactions;
    return transactions.filter(t => t.type === filter);
  }, [transactions, filter]);

  const dateStr = new Date().toLocaleDateString("en-US", {
    weekday: "long", month: "long", day: "numeric",
  });

  return (
    <>
      <Header />

      <Box sx={{ minHeight: "100vh", bgcolor: "#f8fafc" }}>
        {/* Hero banner */}
        <Box sx={{
          background: "linear-gradient(135deg, #0f172a 0%, #1e3a5f 55%, #FF3008 130%)",
          pt: { xs: 10, md: 12 },
          pb: { xs: 14, md: 16 },
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""', position: "absolute", top: -120, right: -120,
            width: 380, height: 380, borderRadius: "50%",
            background: "rgba(255,255,255,0.04)",
          },
          "&::after": {
            content: '""', position: "absolute", bottom: -160, left: -100,
            width: 320, height: 320, borderRadius: "50%",
            background: "rgba(255,255,255,0.03)",
          },
        }}>
          <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
            <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ xs: "flex-start", sm: "center" }} gap={2.5}>
              <Box sx={{
                width: { xs: 52, md: 60 }, height: { xs: 52, md: 60 }, borderRadius: "14px",
                background: "rgba(255,255,255,0.12)", backdropFilter: "blur(8px)",
                border: "1px solid rgba(255,255,255,0.15)",
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0,
              }}>
                <AccountBalanceWalletIcon sx={{ color: "#93c5fd", fontSize: 28 }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{
                  fontWeight: 800, fontSize: { xs: "1.6rem", md: "2.1rem" },
                  color: "#fff", lineHeight: 1.15, letterSpacing: "-0.02em",
                }}>
                  RMF Wallet
                </Typography>
                <Typography sx={{ fontSize: "0.9rem", color: "rgba(255,255,255,0.7)", mt: 0.4 }}>
                  {dateStr} &middot; Credits, refunds & order spend
                </Typography>
              </Box>
            </Stack>
          </Container>
        </Box>

        {/* Pulled-up balance card */}
        <Container maxWidth="lg" sx={{ mt: { xs: -10, md: -12 }, pb: 6, position: "relative", zIndex: 2 }}>
          {/* Headline balance */}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, md: 4 },
              borderRadius: "24px",
              background: "linear-gradient(135deg, #6366f1 0%, #7c3aed 55%, #c026d3 110%)",
              color: "#fff",
              boxShadow: "0 18px 50px rgba(124,58,237,0.32)",
              position: "relative",
              overflow: "hidden",
              "&::before": {
                content: '""', position: "absolute", top: -80, right: -40,
                width: 220, height: 220, borderRadius: "50%",
                background: "rgba(255,255,255,0.08)",
              },
              "&::after": {
                content: '""', position: "absolute", bottom: -100, left: -60,
                width: 240, height: 240, borderRadius: "50%",
                background: "rgba(255,255,255,0.05)",
              },
            }}
          >
            <Stack
              direction={{ xs: "column", md: "row" }}
              alignItems={{ xs: "flex-start", md: "center" }}
              justifyContent="space-between"
              gap={3}
              sx={{ position: "relative", zIndex: 1 }}
            >
              <Box>
                <Typography sx={{
                  fontSize: "0.75rem", fontWeight: 700, letterSpacing: 1, textTransform: "uppercase",
                  color: "rgba(255,255,255,0.7)",
                }}>
                  Available Balance
                </Typography>
                {balLoading ? (
                  <CircularProgress size={32} sx={{ color: "#fff", mt: 1 }} />
                ) : (
                  <Typography sx={{
                    fontSize: { xs: "2.8rem", md: "3.4rem" }, fontWeight: 900,
                    lineHeight: 1, letterSpacing: "-0.03em", mt: 0.5,
                  }}>
                    ${balance.toFixed(2)}
                  </Typography>
                )}
                {nextExpiry && (
                  <Stack direction="row" alignItems="center" gap={0.5} sx={{ mt: 1.5 }}>
                    <AccessTimeIcon sx={{ fontSize: 14, color: "rgba(255,255,255,0.7)" }} />
                    <Typography sx={{ fontSize: "0.8rem", color: "rgba(255,255,255,0.85)", fontWeight: 600 }}>
                      Earliest expiry · {formatDate(nextExpiry.expiresAt)}
                    </Typography>
                  </Stack>
                )}
              </Box>

              <Button
                onClick={() => navigate("/")}
                startIcon={<ShoppingCartIcon />}
                sx={{
                  bgcolor: "#fff",
                  color: "#6d28d9",
                  fontWeight: 800,
                  textTransform: "none",
                  borderRadius: "14px",
                  px: 3, py: 1.5,
                  fontSize: "0.95rem",
                  boxShadow: "0 6px 18px rgba(0,0,0,0.15)",
                  "&:hover": { bgcolor: "#f5f3ff" },
                }}
              >
                Order &amp; use wallet
              </Button>
            </Stack>
          </Paper>

          {/* Store credit: usable only at one restaurant (refunds that were its mistake) */}
          {storeCredits.length > 0 && (
            <Paper elevation={0} sx={{ mt: 2, p: 2.25, borderRadius: "16px", bgcolor: "#fff", border: "1px solid #e5e7eb" }}>
              <Typography sx={{ fontWeight: 800, fontSize: "1rem", color: "#111827" }}>
                Store credit
              </Typography>
              <Typography sx={{ fontSize: "0.8rem", color: "#6b7280", mb: 1.5 }}>
                Only usable at these restaurants. It's applied at checkout before your wallet balance.
              </Typography>
              <Stack spacing={1}>
                {storeCredits.map((c) => (
                  <Stack key={c.restaurantId} direction="row" alignItems="center" gap={1.5}
                    sx={{ p: 1.25, borderRadius: "12px", border: "1px solid #f1f5f9", bgcolor: "#f8fafc" }}>
                    <Box sx={{
                      width: 40, height: 40, borderRadius: "10px", overflow: "hidden", flexShrink: 0,
                      bgcolor: "#f3e8ff", display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      {c.restaurantImage
                        ? <Box component="img" src={resizedImage(c.restaurantImage, 128)} alt="" sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
                        : <StorefrontOutlinedIcon sx={{ color: "#7c3aed", fontSize: 22 }} />}
                    </Box>
                    <Typography sx={{ flex: 1, minWidth: 0, fontWeight: 700, fontSize: "0.9rem", color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {c.restaurantName || "Restaurant"}
                    </Typography>
                    <Typography sx={{ fontWeight: 800, fontSize: "0.95rem", color: "#16a34a" }}>
                      ${parseFloat(c.balance).toFixed(2)}
                    </Typography>
                    {c.restaurantSlug && (
                      <Button size="small" onClick={() => navigate(`/restaurant/${c.restaurantSlug}`)}
                        sx={{ textTransform: "none", fontWeight: 700, color: "#FF3008", borderRadius: "10px", minWidth: 0 }}>
                        Order
                      </Button>
                    )}
                  </Stack>
                ))}
              </Stack>
            </Paper>
          )}

          {/* Stats row */}
          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={6} sm={4}>
              <StatCard
                icon={<ArrowUpwardIcon />}
                label="Total credited"
                value={`$${totals.credits.toFixed(2)}`}
                accent="#16a34a"
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <StatCard
                icon={<ArrowDownwardIcon />}
                label="Total spent"
                value={`$${totals.debits.toFixed(2)}`}
                accent="#dc2626"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <StatCard
                icon={<HistoryIcon />}
                label="Transactions"
                value={transactions.length}
                accent="#6366f1"
              />
            </Grid>
          </Grid>

          {/* How to use */}
          <Paper
            elevation={0}
            sx={{
              mt: 3, p: 2.25, borderRadius: "16px",
              bgcolor: "#eff6ff", border: "1px solid #bfdbfe",
            }}
          >
            <Stack direction="row" alignItems="flex-start" gap={1.5}>
              <Box sx={{
                width: 32, height: 32, borderRadius: "10px",
                bgcolor: "#1d4ed8", display: "flex",
                alignItems: "center", justifyContent: "center", flexShrink: 0,
              }}>
                <InfoOutlinedIcon sx={{ color: "#fff", fontSize: 18 }} />
              </Box>
              <Box>
                <Typography sx={{ fontSize: "0.88rem", fontWeight: 800, color: "#1d4ed8", mb: 0.3 }}>
                  How your wallet works
                </Typography>
                <Typography sx={{ fontSize: "0.82rem", color: "#1e40af", lineHeight: 1.55 }}>
                  At checkout, enter the amount you'd like to use from your wallet.
                  Any remaining balance is charged to your card. Older credits are used first.
                </Typography>
              </Box>
            </Stack>
          </Paper>

          {/* Transaction history */}
          <Box sx={{ mt: 4 }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ xs: "flex-start", sm: "center" }}
              justifyContent="space-between"
              gap={2}
              sx={{ mb: 2 }}
            >
              <Box>
                <Typography sx={{ fontWeight: 800, fontSize: "1.1rem", color: "#111827" }}>
                  Transaction history
                </Typography>
                <Typography sx={{ fontSize: "0.8rem", color: "#6b7280" }}>
                  All wallet activity in chronological order
                </Typography>
              </Box>

              {/* Filter pills */}
              {transactions.length > 0 && (
                <Box sx={{
                  p: 0.5, bgcolor: "#fff", borderRadius: "14px",
                  border: "1px solid #e5e7eb", display: "inline-flex", gap: 0.5,
                  boxShadow: "0 2px 8px rgba(15,23,42,0.04)",
                }}>
                  {FILTERS.map(f => {
                    const Icon = f.icon;
                    const active = filter === f.key;
                    return (
                      <Box
                        key={f.key}
                        onClick={() => setFilter(f.key)}
                        sx={{
                          display: "inline-flex", alignItems: "center", gap: 0.6,
                          px: 1.5, py: 0.65, borderRadius: "10px", cursor: "pointer",
                          bgcolor: active ? "#FF3008" : "transparent",
                          color: active ? "#fff" : "#475569",
                          transition: "all 0.15s ease",
                          "&:hover": { bgcolor: active ? "#FF3008" : "#f1f5f9" },
                        }}
                      >
                        <Icon sx={{ fontSize: 14 }} />
                        <Typography sx={{ fontSize: "0.78rem", fontWeight: 700 }}>
                          {f.label}
                        </Typography>
                        <Box sx={{
                          bgcolor: active ? "rgba(255,255,255,0.22)" : "#e5e7eb",
                          color: active ? "#fff" : "#475569",
                          fontSize: "0.68rem", fontWeight: 800,
                          px: 0.6, py: 0.05, borderRadius: "8px",
                          minWidth: 20, textAlign: "center",
                        }}>
                          {counts[f.key]}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Stack>

            {txLoading && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                <CircularProgress sx={{ color: "#FF3008" }} />
              </Box>
            )}

            {!txLoading && transactions.length === 0 && (
              <Box sx={{
                textAlign: "center", py: 8, bgcolor: "#fff", borderRadius: "16px",
                border: "1px solid #e5e7eb",
              }}>
                <Box sx={{
                  width: 72, height: 72, borderRadius: "50%",
                  bgcolor: "#f3e8ff", display: "inline-flex",
                  alignItems: "center", justifyContent: "center", mb: 1.5,
                }}>
                  <AccountBalanceWalletIcon sx={{ fontSize: 36, color: "#7c3aed" }} />
                </Box>
                <Typography sx={{ fontSize: "1rem", fontWeight: 800, color: "#111827", mb: 0.5 }}>
                  No transactions yet
                </Typography>
                <Typography sx={{ fontSize: "0.85rem", color: "#6b7280" }}>
                  Wallet credits from refunds and promotions will show up here.
                </Typography>
              </Box>
            )}

            {!txLoading && transactions.length > 0 && filtered.length === 0 && (
              <Box sx={{ textAlign: "center", py: 6, bgcolor: "#fff", borderRadius: "16px", border: "1px solid #e5e7eb" }}>
                <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color: "#475569" }}>
                  No {filter === "CREDIT" ? "credits" : "debits"} in this view
                </Typography>
              </Box>
            )}

            <Stack spacing={2}>
              {filtered.map(tx => <TxCard key={tx.id} tx={tx} />)}
            </Stack>
          </Box>
        </Container>
      </Box>

    </>
  );
}
